import TableHeader_wanted from "@/components/feature/rosterTable/TableHeader_wanted";
import TableBody_wanted from "@/components/feature/rosterTable/TableBody_wanted";
import "@css/modal_submittedWantedList.css";
import type * as type from "@vTypes";
import { useMemo } from "react";
import { X } from "lucide-react";
import { useIsAdmin } from "@/hooks/useAuth.ts";
import { useGroupStore } from "@/store/groupStore.ts";
import { useQuery } from "@tanstack/react-query";
import { weeklyOffNurses } from "@api/nurses";
import type { NurseResponse } from "@/types/nurse-type";
import usePreferenceSchedule from "@/hooks/roster-craete/usePreferenceSchedule";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import { useRosterSubmissionQuery } from "@/hooks/roster-craete/useRosterSubmissionQuery";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { useWantedShiftRequestsQuery } from "@/hooks/roster-wanted/useWantedShiftRequestsQuery";
import { mapShiftRequestsToPreferenceSchedule } from "@/utils/mapShiftRequestsToPreferenceSchedule";

interface ModalOffRequestListProps {
  defaultShiftCode: type.availableShifts[];
  currentScheduleInfo: { year: number; month: number };
  holidayDates: string[] | null;
  nurses: NurseResponse[] | null;
  shiftInfo: type.availableShifts[];
  fullWanted: type.WantedAll[];
  onClose: () => void;
}

export default function ModalOffRequestList({
  defaultShiftCode,
  currentScheduleInfo,
  holidayDates,
  nurses,
  shiftInfo,
  fullWanted,
  onClose,
}: ModalOffRequestListProps) {
  const isAdminHook = useIsAdmin();
  const { selectedGroupId: selectedGroupIdFromStore } = useGroupStore();

  // preferenceSchedule을 모달 내부에서 직접 가져오기
  const { data: preferenceSchedule, isPending } = usePreferenceSchedule({
    isAdmin: isAdminHook,
    selectedGroupId: selectedGroupIdFromStore,
    curDate: {
      year: currentScheduleInfo.year,
      month: currentScheduleInfo.month,
    },
    fullNurseSchema: nurses ?? [],
  });

  // 제출된 간호사 목록을 직접 가져오기
  const { data: submissionData, isLoading: isSubmissionLoading } =
    useRosterSubmissionQuery({
      year: currentScheduleInfo.year,
      month: currentScheduleInfo.month,
      groupId: isAdminHook ? (selectedGroupIdFromStore ?? null) : null,
    });
  const currentSubmittedList = submissionData?.submitted_nurses ?? [];

  const { data: shiftsData } = useShiftsQuery(
    isAdminHook ? (selectedGroupIdFromStore ?? undefined) : undefined
  );
  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    defaultShiftCode,
    shiftsData ?? null
  );

  const { data: shiftRequestsData, isLoading: isShiftRequestsLoading } =
    useWantedShiftRequestsQuery({
      year: currentScheduleInfo.year,
      month: currentScheduleInfo.month,
      groupId: isAdminHook ? (selectedGroupIdFromStore ?? null) : null,
      enabled: !!(
        nurses?.length &&
        currentScheduleInfo.year &&
        currentScheduleInfo.month
      ),
    });

  const preferenceScheduleForTable = useMemo(() => {
    const base = preferenceSchedule ?? [];
    const results = shiftRequestsData?.results;
    if (!nurses?.length || !results?.length) {
      return base;
    }
    return mapShiftRequestsToPreferenceSchedule(
      nurses,
      base,
      results,
      currentScheduleInfo.year,
      currentScheduleInfo.month,
      shiftLookupMaps
    );
  }, [
    nurses,
    preferenceSchedule,
    shiftRequestsData,
    currentScheduleInfo.year,
    currentScheduleInfo.month,
    shiftLookupMaps,
  ]);

  // 주휴 데이터 가져오기
  const { data: weeklyOffData } = useQuery({
    queryKey: [
      "weeklyOffNurses",
      currentScheduleInfo.year,
      currentScheduleInfo.month,
      isAdminHook ? (selectedGroupIdFromStore ?? undefined) : undefined,
    ],
    queryFn: () =>
      weeklyOffNurses({
        year: currentScheduleInfo.year,
        month: currentScheduleInfo.month,
        groupId: isAdminHook
          ? (selectedGroupIdFromStore ?? undefined)
          : undefined,
      }),
    enabled: !!currentScheduleInfo.year && !!currentScheduleInfo.month,
  });

  return (
    <div
      className="absolute  bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ModalSubmittedWantedList us-n z-100!"
      // ref={ref_modal}
    >
      <div className="modal_moveHandler text-[20px] font-pretendard font-semibold text-center px-[60px] pt-[29px] text-[#333333]">
        <h3 className="">
          전체 근무자 <span className="text-sig">OFF 요청 현황</span>
        </h3>
        <button
          type="button"
          aria-label="close"
          onClick={onClose}
          className="absolute top-3 right-3"
        >
          <X size={32} strokeWidth={2} className="text-[#939399] " />
        </button>
      </div>
      <div className="modal_wantedList_body">
        <div className="relative">
          {(isPending || isSubmissionLoading || isShiftRequestsLoading) && (
            <div className="absolute pointer-events-auto inset-0 flex items-center justify-center bg-white/50  z-50">
              <LoadingSpiner msg="전체 근무자 OFF 현황을 가져오는 중입니다." />
            </div>
          )}
          <div className="rosterTable">
            <TableHeader_wanted
              defaultShiftCode={defaultShiftCode}
              nursesCount={nurses?.length ?? 0}
              currentYear={currentScheduleInfo.year}
              currentMonth={currentScheduleInfo.month}
              holidayDates={holidayDates ?? []}
              onlyView={true}
              prefix="offRequestView-"
            />

            <TableBody_wanted
              nurses={nurses}
              shiftInfo={shiftInfo}
              currentSubmittedList={currentSubmittedList}
              fullWanted={fullWanted}
              preferenceSchedule={preferenceScheduleForTable}
              isViewOnlyOff={true}
              currentYear={currentScheduleInfo.year}
              currentMonth={currentScheduleInfo.month}
              showWeeklyOff={false}
              weeklyOffData={weeklyOffData}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
